import {
  Alert,
  Box,
  Button,
  Container,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { CognitoUser } from "amazon-cognito-identity-js";
import React, { useState } from "react";
import { completeNewPassword, MfaSetupRequiredResult } from "./auth";

interface NewPasswordProps {
  cognitoUser: CognitoUser;
  onPasswordSet: (result?: MfaSetupRequiredResult) => void;
  onCancel: () => void;
}

export const NewPassword: React.FC<NewPasswordProps> = ({
  cognitoUser,
  onPasswordSet,
  onCancel,
}) => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const validatePassword = (password: string): string | null => {
    if (password.length < 8) {
      return "Password must be at least 8 characters long";
    }
    if (!/[A-Z]/.test(password)) {
      return "Password must contain at least one uppercase letter";
    }
    if (!/[a-z]/.test(password)) {
      return "Password must contain at least one lowercase letter";
    }
    if (!/[0-9]/.test(password)) {
      return "Password must contain at least one number";
    }
    if (!/[^A-Za-z0-9]/.test(password)) {
      return "Password must contain at least one special character";
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    // Validate password requirements
    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    setLoading(true);

    try {
      const result = await completeNewPassword(cognitoUser, newPassword);
      if (result.type === "MFA_SETUP_REQUIRED") {
        onPasswordSet(result);
      } else {
        onPasswordSet();
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to set new password",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Paper elevation={3} sx={{ p: 4, width: "100%" }}>
          <Typography component="h1" variant="h5" align="center" gutterBottom>
            Set New Password
          </Typography>
          <Typography
            variant="body2"
            align="center"
            color="text.secondary"
            sx={{ mb: 3 }}
          >
            Your temporary password has expired. Please set a new password.
          </Typography>

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              name="newPassword"
              label="New Password"
              type="password"
              id="newPassword"
              autoFocus
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Confirm Password"
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />

            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Password requirements:
              </Typography>
              <Typography
                variant="caption"
                display="block"
                color="text.secondary"
              >
                • At least 8 characters
              </Typography>
              <Typography
                variant="caption"
                display="block"
                color="text.secondary"
              >
                • At least one uppercase letter
              </Typography>
              <Typography
                variant="caption"
                display="block"
                color="text.secondary"
              >
                • At least one lowercase letter
              </Typography>
              <Typography
                variant="caption"
                display="block"
                color="text.secondary"
              >
                • At least one number
              </Typography>
              <Typography
                variant="caption"
                display="block"
                color="text.secondary"
              >
                • At least one special character
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? "Setting password..." : "Set Password"}
            </Button>

            <Button
              fullWidth
              variant="text"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};
